import React, { useEffect, useMemo, useRef, useState } from "react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import type { CohortMemberView, CohortView } from "@shared/prospectiveCohort";

type PanelMode = "current" | "historical";
type LoadedView = { key: string; value: CohortView };

const missingStatuses = ["missing-baseline", "missing-endpoint", "missing-both"] as const;
const count = (values: Record<string, number> | undefined, key: string) => values?.[key] ?? 0;
const totalMissing = (values: Record<string, number> | undefined) => missingStatuses.reduce((sum, key) => sum + count(values, key), 0);

function validUtcBoundary(value: string) {
  if (!/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{3})?Z$/.test(value)) return false;
  const time = Date.parse(value);
  return Number.isFinite(time) && new Date(time).toISOString().replace(/\.000Z$/, "Z") === value.replace(/\.000Z$/, "Z");
}

function utcLabel(value: string | null | undefined) {
  if (!value) return "Not recorded";
  const time = Date.parse(value);
  return Number.isFinite(time) ? new Date(time).toISOString().replace("T", " ").replace(".000Z", " UTC").replace(/Z$/, " UTC") : "Not recorded";
}

function memberLabel(member: CohortMemberView) {
  const admission = member.admission;
  return admission?.instrument?.symbol ?? member.population.requestedAsset ?? member.population.jobId;
}

function saveJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(href);
}

export default function ProspectiveCohortPanel() {
  const [mode, setMode] = useState<PanelMode>("current");
  const [boundary, setBoundary] = useState("");
  const [refreshGeneration, setRefreshGeneration] = useState(0);
  const [loaded, setLoaded] = useState<LoadedView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailJobId, setDetailJobId] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const key = mode === "current" ? "live" : `historical:${boundary}`;
  const requestKey = `${key}:generation-${refreshGeneration}`;
  const activeKey = useRef(requestKey);
  activeKey.current = requestKey;
  const boundaryValid = mode === "current" || validUtcBoundary(boundary);
  const view = loaded?.key === requestKey ? loaded.value : null;
  const queryUrl = useMemo(() => mode === "current"
    ? "/api/research/cohort"
    : `/api/research/cohort?asOf=${encodeURIComponent(boundary)}`, [mode, boundary]);

  useEffect(() => {
    setLoaded(null);
    setError(null);
    setDetailJobId(null);
    setDetailError(null);
    if (!boundaryValid) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const requestIdentity = requestKey;
    setLoading(true);
    void (async () => {
      try {
        const response = await fetch(queryUrl, {
          headers: getClientIdentityHeaders(),
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail ?? data.error ?? `Could not load cohort (HTTP ${response.status})`);
        if (activeKey.current === requestIdentity) setLoaded({ key: requestIdentity, value: data as CohortView });
      } catch (reason) {
        if (!controller.signal.aborted && activeKey.current === requestIdentity) {
          setError(reason instanceof Error ? reason.message : "Could not load cohort");
        }
      } finally {
        if (activeKey.current === requestIdentity) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [requestKey, queryUrl, boundaryValid]);

  const loadMember = async (member: CohortMemberView) => {
    const jobId = member.population.jobId;
    setDetailJobId(jobId);
    setDetailError(null);
    try {
      const asOf = view ? `?asOf=${encodeURIComponent(view.asOf)}` : "";
      const response = await fetch(`/api/research/cohort/members/${encodeURIComponent(jobId)}${asOf}`, {
        headers: getClientIdentityHeaders(),
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail ?? data.error ?? `Could not load member (HTTP ${response.status})`);
      if (activeKey.current === requestKey) saveJson(data, `tsm-cohort-${jobId}.json`);
    } catch (reason) {
      if (activeKey.current === requestKey) setDetailError(reason instanceof Error ? reason.message : "Could not export member evidence");
    } finally {
      if (activeKey.current === requestKey) setDetailJobId(null);
    }
  };

  const admissions = view?.admissionCounts;
  const observations = view?.observationCounts;

  return <section className="reports-section prospective-cohort-panel" aria-labelledby="prospective-cohort-title">
    <div className="reports-section-heading">
      <h2 id="prospective-cohort-title">Digital Asset Trader outcome pilot</h2>
      <span>Private · descriptive</span>
    </div>
    <p>This is a fixed 30-day UTC pilot for newly saved, eligible Digital Asset Trader assessments. It describes sampled Bitget spot midpoint movement over 15 minutes. It does not establish accuracy, profitability, or a trading advantage.</p>
    <p>Baseline and endpoint are sampled in declared 60-second UTC windows. The underlying order-book update age is unknown. Cost scenarios are assumptions; fills, depth, slippage, funding, and borrow are not measured. Short direction is a spot counterfactual; borrowing and executable short availability are not measured.</p>

    <fieldset className="reports-archive-filter">
      <legend>View boundary</legend>
      <label><input type="radio" name="cohort-view-mode" checked={mode === "current"} onChange={() => setMode("current")} /> Current</label>{" "}
      <label><input type="radio" name="cohort-view-mode" checked={mode === "historical"} onChange={() => setMode("historical")} /> Historical UTC</label>
        {mode === "current" && <button type="button" className="analytics-action-btn" onClick={() => setRefreshGeneration((generation) => generation + 1)} disabled={loading}>Refresh snapshot</button>}
      {mode === "historical" && <div>
        <label htmlFor="cohort-as-of">As of (UTC ISO timestamp)</label>{" "}
        <input id="cohort-as-of" type="text" inputMode="text" placeholder="2026-10-12T12:00:00Z" value={boundary} onChange={(event) => setBoundary(event.target.value)} aria-describedby="cohort-boundary-help" />
        <button type="button" className="analytics-action-btn" onClick={() => { setBoundary(""); setMode("current"); }}>Clear historical boundary</button>
        <p id="cohort-boundary-help">Enter a UTC timestamp ending in Z. Historical views include only records retained by that boundary.</p>
        {boundary.length > 0 && !boundaryValid && <p role="alert">Enter a valid UTC timestamp, for example 2026-10-12T12:00:00Z.</p>}
      </div>}
    </fieldset>

    {loading && <p role="status">Loading private cohort view…</p>}
    {error && <p role="alert">Cohort view unavailable: {error}</p>}
    {!loading && !error && !view && <p className="reports-empty">No cohort statistics are available until the private view loads.</p>}

    {view && <>
      <p><strong>{mode === "historical" ? "Historical snapshot" : "Current snapshot"}</strong> · As of {utcLabel(view.asOf)} · {view.activation ? (view.preliminary ? "Preliminary · descriptive only" : "Pilot closed · descriptive only") : "Not active at this boundary · descriptive only"} · {view.populationCount} population records</p>
      {view.activation ? <p>Definition: {view.activation.definition.id} · {view.activation.definition.version} · Bitget spot · 15-minute sampled horizon · Pilot window {utcLabel(view.activation.activatedAt)} to {utcLabel(view.activation.closesAt)}</p> : <p>No pilot activation is recorded for this view.</p>}
      <div className="home-status-strip" aria-label="Cohort counts">
        <div><span>Admitted</span><strong>{count(admissions, "admitted")}</strong></div>
        <div><span>Pending</span><strong>{count(observations, "pending")}</strong></div>
        <div><span>Completed</span><strong>{count(observations, "complete")}</strong></div>
        <div><span>Missing</span><strong>{totalMissing(observations)}</strong></div>
        <div><span>Invalid admission</span><strong>{count(admissions, "invalid")}</strong></div>
        <div><span>Invalid observation</span><strong>{count(observations, "invalid")}</strong></div>
        <div><span>Abstained</span><strong>{count(admissions, "abstained")}</strong></div>
        <div><span>Unresolved</span><strong>{count(admissions, "unresolved")}</strong></div>
      </div>
      <p>Other admission outcomes: incomplete {count(admissions, "incomplete")} · unsupported instrument {count(admissions, "unsupported-instrument")} · stale assessment {count(admissions, "stale-assessment")} · job failed {count(admissions, "job-failed")}.</p>
      {view.activation && <details>
        <summary>Definition and method</summary>
        <p>{view.activation.definition.question}</p>
        <p>Provider: Bitget Classic spot ticker API; actual admitted instrument is retained per member. Sample windows are baseline [t0, t0 + 60s) and endpoint [t0 + 15m, t0 + 16m). Only complete pairs have an outcome. Ticker-reported snapshot time and receipt time are separate; underlying book update age is unknown.</p>
        <p>Interpretation: {view.activation.definition.interpretation}</p>
        <details><summary>Evidence details</summary>
          <p>Definition digest: {view.activation.definitionDigest}</p>
          <p>Admission policy: {view.activation.definition.admissionPolicy}</p>
          <p>Quote policy: {view.activation.definition.quotePolicy}</p>
          <p>Midpoint formula: {view.activation.definition.midpointFormula}</p>
          <p>Crossing formula: {view.activation.definition.crossingFormula}</p>
          <p>Sample policy: {view.activation.definition.samplePolicy}</p>
        </details>
      </details>}
      <div className="reports-section-heading"><h3>Population records</h3><span>{view.members.length} shown</span></div>
      {view.members.length ? <div className="reports-grid">
        {view.members.map((member) => <article className="reports-card" key={member.population.jobId}>
          <h4>{memberLabel(member)}</h4>
          <p>Admission: {member.admission?.status ?? "Pending job resolution"} · Observation: {member.status}</p>
          <p>Enrolled: {utcLabel(member.population.enrolledAt)} · Admitted: {utcLabel(member.admission?.admittedAt)}</p>
          {member.admission && <p>Direction: {member.admission.direction ?? "Not admitted as directional"}{member.admission.direction === "short" ? " · Short is a directional spot counterfactual, not an executable-short claim." : ""}</p>}
          {member.admission?.instrument && <p>Actual instrument: {member.admission.instrument.venue} {member.admission.instrument.market_type} · {member.admission.instrument.symbol} ({member.admission.instrument.providerSymbol})</p>}
          {member.outcome && <>
            <p>Descriptive movement: {member.outcome.signedMovementBps.toFixed(2)} bps signed · always-long comparison {member.outcome.alwaysLongMovementBps.toFixed(2)} bps · sampled elapsed {Math.round(member.outcome.elapsedMs / 1000)} seconds.</p>
            <p>Baseline sample: {member.baseline ? `${member.baseline.bid} / ${member.baseline.ask} at ${utcLabel(member.baseline.observedAt)}` : "Missing"} · Endpoint sample: {member.endpoint ? `${member.endpoint.bid} / ${member.endpoint.ask} at ${utcLabel(member.endpoint.observedAt)}` : "Missing"}.</p>
            <p>Bid/ask crossing sensitivity: {member.outcome.crossingBps.toFixed(2)} bps before assumed costs.</p>
            <p>Crossing after assumed round-trip costs: {member.outcome.costScenarios.map((scenario) => `${scenario.roundTripCostBps} bps cost → ${scenario.crossingLessCostBps.toFixed(2)} bps`).join(" · ")}.</p>
          </>}
          {member.overlappingMembers > 0 && <p>Overlapping same-instrument assessments: {member.overlappingMembers}. These records are retained and may not be independent.</p>}
          {member.reasons.length > 0 ? <ul aria-label="Observation reasons">{member.reasons.map((reason, index) => <li key={`${reason}-${index}`}>{reason === "provider-failure" ? "The Bitget quote request failed." : reason === "invalid-provider-snapshot-clock" ? "The provider snapshot time was missing or outside the declared sampling window." : reason === "invalid-bid-ask" ? "The returned bid and ask did not pass quote validation." : reason === "attempt-clock-outside-window" ? "The quote request or receipt fell outside its declared sampling window." : reason === "quote-symbol-mismatch" ? "The provider returned a different market symbol." : reason === "no-attempts-missed-window" ? "No quote attempt was retained for the required sampling window." : reason === "invalid-midpoint" ? "The midpoint could not be calculated from a valid quote." : reason.replace(/[_-]+/g, " ")}</li>)}</ul>
            : member.status.startsWith("missing-") && member.attempts.length === 0 && <p>No quote attempt was retained for the required sampling window.</p>}
          <p>Result status only; repeated or overlapping assessments are retained. No accuracy or profit claim is made.</p>
          <button type="button" className="analytics-action-btn" onClick={() => void loadMember(member)} disabled={detailJobId === member.population.jobId}>
            {detailJobId === member.population.jobId ? "Preparing…" : "Download retained member JSON"}
          </button>
        </article>)}
      </div> : <div className="reports-empty">No population records are present at this boundary.</div>}
      {detailError && <p role="alert">Member export unavailable: {detailError}</p>}
    </>}
    <p>Historical media or future outcomes are not reconstructed. This panel reads retained records only; it does not start collection or research jobs.</p>
  </section>;
}
